import { matchPath } from "react-router-dom";
import { lazyWithRetry } from "@/utils/lazy-with-retry";
import Home from "@/pages/Home";
import {
  ACCOUNT_SETTINGS,
  BASICS,
  CAMPAIGN_LANDING,
  CHAT,
  COLLECTIONS,
  COLLECTION_DETAIL,
  DELETE_ACCOUNT,
  DISCOVER,
  EXPLORE,
  DONE,
  EDIT_PROFILE,
  EVENTS,
  FAVORITES,
  HOME,
  MAP,
  NOTIFICATIONS,
  NOTIFICATION_SETTINGS,
  HOME_LAYOUT,
  PROFILE,
  ROUNDUPS,
  ROUNDUP_DETAIL,
  SEARCH,
  SETUP,
  STASH_DETAIL,
  STASH_TAG_BROWSE,
  STASHES,
  STUFF_DETAIL,
  TAGS,
} from "@/routes";
import type { StackedRouteEntry, StackedRouteHandle, TabId, TabRouteEntry } from "./appShellRouteTypes";

const Stashes = lazyWithRetry(() => import("@/pages/Stashes"));
const Collections = lazyWithRetry(() => import("@/pages/Collections"));
const FavoritesPage = lazyWithRetry(() =>
  import("@/pages/PersonalCollectionPage").then(module => ({ default: module.FavoritesPage })),
);
const DonePage = lazyWithRetry(() =>
  import("@/pages/PersonalCollectionPage").then(module => ({ default: module.DonePage })),
);
const TagsPage = lazyWithRetry(() => import("@/pages/Tags"));
const Events = lazyWithRetry(() => import("@/pages/Events"));
const MapPage = lazyWithRetry(() => import("@/pages/Map"));
const Discoveries = lazyWithRetry(() => import("@/pages/Discoveries"));
const Explore = lazyWithRetry(() => import("@/pages/Explore"));
const ChatPage = lazyWithRetry(() => import("@/pages/StashtChat"));
const SearchPage = lazyWithRetry(() => import("@/pages/Search"));
const Profile = lazyWithRetry(() => import("@/pages/Profile"));
const PreferencesRedirect = lazyWithRetry(() =>
  import("@/features/profile/ProfileTabRedirect").then(module => ({ default: module.PreferencesRedirect })));
const NotificationsRedirect = lazyWithRetry(() =>
  import("@/features/profile/ProfileTabRedirect").then(module => ({ default: module.NotificationsRedirect })));
const DeletionRedirect = lazyWithRetry(() =>
  import("@/features/profile/ProfileTabRedirect").then(module => ({ default: module.DeletionRedirect })));
const Notifications = lazyWithRetry(() => import("@/pages/Notifications"));
const HomeLayoutEditor = lazyWithRetry(() => import("@/pages/HomeLayoutEditor"));
const RoundupArchive = lazyWithRetry(() => import("@/pages/RoundupArchive"));
const RoundupDetail = lazyWithRetry(() => import("@/pages/RoundupDetail"));
const StashDetailPage = lazyWithRetry(() => import("@/pages/StashDetailPage"));
const StashTagBrowsePage = lazyWithRetry(() => import("@/pages/StashTagBrowsePage"));
const StuffDetailPage = lazyWithRetry(() => import("@/pages/StuffDetailPage"));
const Setup = lazyWithRetry(() => import("@/pages/Setup"));
const Basics = lazyWithRetry(() => import("@/pages/Basics"));
const CollectionDetailPage = lazyWithRetry(() => import("@/pages/CollectionDetailPage"));
const CampaignLandingPage = lazyWithRetry(() => import("@/pages/CampaignLandingPage"));

const tabRoute = ({
  path,
  tabId,
  component,
  reselectAction,
}: {
  path: string;
  tabId: TabId;
  component: NonNullable<StackedRouteHandle["component"]>;
  reselectAction?: StackedRouteHandle["reselectAction"];
}): TabRouteEntry => ({
  path,
  handle: {
    type: "tab",
    tabId,
    tabPath: path,
    component,
    reselectAction,
  },
});

const stackRoute = ({
  path,
  component,
}: {
  path: string;
  component: NonNullable<StackedRouteHandle["component"]>;
}): StackedRouteEntry => ({
  path,
  handle: {
    type: "stack",
    component,
  },
});

export const APP_SHELL_ROUTES: StackedRouteEntry[] = [
  tabRoute({
    path: HOME(),
    tabId: "home",
    component: Home,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: STASHES(),
    tabId: "stashes",
    component: Stashes,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: COLLECTIONS(),
    tabId: "collections",
    component: Collections,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: FAVORITES(),
    tabId: "favorites",
    component: FavoritesPage,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: DONE(),
    tabId: "done",
    component: DonePage,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: TAGS(),
    tabId: "tags",
    component: TagsPage,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: EVENTS(),
    tabId: "events",
    component: Events,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: MAP(),
    tabId: "map",
    component: MapPage,
    reselectAction: "recenter",
  }),
  tabRoute({
    path: DISCOVER(),
    tabId: "discover",
    component: Discoveries,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: EXPLORE(),
    tabId: "explore",
    component: Explore,
    reselectAction: "scrollTop",
  }),
  tabRoute({
    path: CHAT(),
    tabId: "chat",
    component: ChatPage,
  }),
  tabRoute({
    path: SEARCH(),
    tabId: "search",
    component: SearchPage,
    reselectAction: "focusInput",
  }),
  tabRoute({
    path: PROFILE(),
    tabId: "profile",
    component: Profile,
    reselectAction: "scrollTop",
  }),
  stackRoute({
    path: STASH_DETAIL(),
    component: StashDetailPage,
  }),
  stackRoute({
    path: STASH_TAG_BROWSE(),
    component: StashTagBrowsePage,
  }),
  stackRoute({
    path: STUFF_DETAIL(),
    component: StuffDetailPage,
  }),
  stackRoute({
    path: COLLECTION_DETAIL(),
    component: CollectionDetailPage,
  }),
  stackRoute({
    path: CAMPAIGN_LANDING(),
    component: CampaignLandingPage,
  }),
  stackRoute({
    path: EDIT_PROFILE(),
    component: PreferencesRedirect,
  }),
  stackRoute({
    path: ACCOUNT_SETTINGS(),
    component: PreferencesRedirect,
  }),
  tabRoute({
    path: NOTIFICATIONS(),
    tabId: "notifications",
    component: Notifications,
    reselectAction: "scrollTop",
  }),
  stackRoute({
    path: NOTIFICATION_SETTINGS(),
    component: NotificationsRedirect,
  }),
  stackRoute({
    path: HOME_LAYOUT(),
    component: HomeLayoutEditor,
  }),
  tabRoute({
    path: ROUNDUPS(),
    tabId: "roundups",
    component: RoundupArchive,
    reselectAction: "scrollTop",
  }),
  stackRoute({
    path: ROUNDUP_DETAIL(),
    component: RoundupDetail,
  }),
  stackRoute({
    path: DELETE_ACCOUNT(),
    component: DeletionRedirect,
  }),
  stackRoute({
    path: SETUP(),
    component: Setup,
  }),
  stackRoute({
    path: BASICS(),
    component: Basics,
  }),
];

export const TAB_ROUTES = APP_SHELL_ROUTES.filter(
  (route): route is TabRouteEntry =>
    route.handle.type === "tab" && !!route.handle.tabId && !!route.handle.tabPath && !!route.handle.component,
);

export const STACK_ROUTES = APP_SHELL_ROUTES.filter(
  (route): route is StackedRouteEntry => route.handle.type === "stack" && !!route.handle.component,
);

export const TAB_ID_TO_PATH = TAB_ROUTES.reduce(
  (acc, route) => {
    acc[route.handle.tabId] = route.handle.tabPath;
    return acc;
  },
  {} as Record<TabId, string>,
);

export const getTabRouteById = (tabId: TabId) => TAB_ROUTES.find(route => route.handle.tabId === tabId) ?? null;

export const getTabRouteByPath = (pathname: string) =>
  TAB_ROUTES.find(route => matchPath(route.path, pathname)) ?? null;

export const getTabIdFromPath = (pathname: string): TabId | null => {
  const match = getTabRouteByPath(pathname);
  return match?.handle.tabId ?? null;
};

export const getTabReselectAction = (tabId: TabId) => {
  const tabRoute = getTabRouteById(tabId);
  return tabRoute?.handle.reselectAction ?? null;
};
